import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ConnectionQuality,
  Room,
  RoomEvent,
  Track,
  TrackEvent,
  createLocalAudioTrack,
  createLocalScreenTracks,
  createLocalVideoTrack,
} from 'livekit-client';
import { API_BASE_URL as API_URL } from '../apiBase';
import { useLanguage } from './LanguageContext';
import { participantIdentity } from '../components/meetings/MeetingIdentity';

const MeetingSessionContext = createContext(null);

function translateRoomError(raw, t) {
  const msg = String(raw || '').trim();
  if (!msg) return t('meetings.room.err.join');
  if (/livekit|LIVEKIT|not configured|não configurado|no configurado/i.test(msg)) {
    return t('meetings.room.err.livekit');
  }
  if (/signal|timed out|timeout|connection has timed/i.test(msg)) {
    return t('meetings.room.err.signalTimeout');
  }
  if (/could not establish|failed to connect|connection failed|disconnect|websocket|pc connection/i.test(msg)) {
    return t('meetings.room.err.connection');
  }
  if (/NetworkError|Failed to fetch|net::/i.test(msg)) {
    return t('meetings.room.err.connection');
  }
  return msg;
}

function mediaErrPt(err) {
  const raw = String(err?.message || err || '');
  if (/NotAllowed|Permission|PermissionDenied|denied by user|Permission dismissed/i.test(raw)) {
    return 'permissão negada ou cancelada no navegador';
  }
  if (/NotFound|DevicesNotFound|Requested device not found/i.test(raw)) {
    return 'nenhum dispositivo encontrado';
  }
  if (/NotReadable|Could not start video|TrackStartError|AbortError/i.test(raw)) {
    return 'dispositivo em uso ou indisponível';
  }
  if (/Overconstrained/i.test(raw)) {
    return 'configuração da câmera não suportada';
  }
  if (/getDisplayMedia|display-capture|Screen share/i.test(raw)) {
    return 'o navegador bloqueou o compartilhamento de tela';
  }
  return 'indisponível neste momento';
}

const attachedMediaTracks = new WeakMap();
function detachEl(el) {
  if (!el) return;
  try {
    attachedMediaTracks.get(el)?.detach(el);
    attachedMediaTracks.delete(el);
    el.srcObject = null;
  } catch {
    /* ignore */
  }
}

function attachTo(track, el) {
  if (!el || !track) return;
  try {
    track.attach(el);
    attachedMediaTracks.set(el, track);
  } catch {
    /* ignore */
  }
}

function qualityRank(q) {
  if (q === ConnectionQuality.Excellent) return 'excellent';
  if (q === ConnectionQuality.Good) return 'good';
  if (q === ConnectionQuality.Poor) return 'poor';
  if (q === ConnectionQuality.Lost) return 'lost';
  return 'unknown';
}

function roomPath(mode, id) {
  return mode === 'live' ? `/reunioes/live/${id}` : `/reunioes/sala/${id}`;
}

function parseBitrateKbps(report, direction) {
  let bytes = 0;
  let packetsLost = 0;
  let rtt = 0;
  report.forEach((s) => {
    if (direction === 'out' && s.type === 'outbound-rtp' && typeof s.bytesSent === 'number') {
      bytes += s.bytesSent;
    }
    if (direction === 'in' && s.type === 'inbound-rtp' && typeof s.bytesReceived === 'number') {
      bytes += s.bytesReceived;
      if (typeof s.packetsLost === 'number') packetsLost += s.packetsLost;
    }
    if (s.type === 'candidate-pair' && s.state === 'succeeded' && typeof s.currentRoundTripTime === 'number') {
      rtt = Math.round(s.currentRoundTripTime * 1000);
    }
  });
  return { bytes, packetsLost, rtt };
}

export function MeetingSessionProvider({ children }) {
  const { t } = useLanguage();
  const roomRef = useRef(null);
  const screenTracksRef = useRef([]);
  const mediaTargetsRef = useRef({});
  const floatVideoRef = useRef(null);
  const bytesPrevRef = useRef({ out: 0, in: 0, t: 0 });
  const chatIdRef = useRef(0);
  const connectKeyRef = useRef('');
  const connectInFlightRef = useRef(null);
  const statusRef = useRef('idle');

  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState('idle');
  const [fatalError, setFatalError] = useState('');
  const [warn, setWarn] = useState('');
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [stageKind, setStageKind] = useState('idle');
  const [remoteCamOn, setRemoteCamOn] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [hands, setHands] = useState([]);
  const [chat, setChat] = useState([]);
  const [unreadChat, setUnreadChat] = useState(0);
  const [network, setNetwork] = useState({
    quality: 'unknown',
    uplinkKbps: 0,
    downlinkKbps: 0,
    packetLoss: 0,
    rttMs: 0,
  });
  const [minimized, setMinimized] = useState(false);
  const [dockExpanded, setDockExpanded] = useState(true);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const syncLayout = useCallback(() => {
    const room = roomRef.current;
    if (!room) return;
    const lp = room.localParticipant;
    const localScreen = lp?.getTrackPublication(Track.Source.ScreenShare)?.track;
    const localCam = lp?.getTrackPublication(Track.Source.Camera)?.track;

    let remoteScreen = null;
    let remoteCam = null;
    room.remoteParticipants.forEach((p) => {
      const s = p.getTrackPublication(Track.Source.ScreenShare)?.track;
      const c = p.getTrackPublication(Track.Source.Camera)?.track;
      if (s && !remoteScreen) remoteScreen = s;
      if (c && !remoteCam) remoteCam = c;
    });

    const { stage, localCam: localEl, remoteCam: remoteEl, pipStage, pipCam } = mediaTargetsRef.current;
    const floatEl = floatVideoRef.current;

    detachEl(stage);
    detachEl(localEl);
    detachEl(remoteEl);
    detachEl(floatEl);
    detachEl(pipStage);
    detachEl(pipCam);

    const stageTrack = remoteScreen || localScreen || remoteCam || null;
    // The floating preview prioritizes your own presentation. Muted elements
    // avoid duplicate audio; the existing room audio remains in the main page.
    const pipTrack = localScreen || stageTrack;
    if (pipTrack && pipStage) attachTo(pipTrack, pipStage);
    if (localCam && pipCam) attachTo(localCam, pipCam);
    if (stage && stageTrack) attachTo(stageTrack, stage);
    if (floatEl && stageTrack) attachTo(stageTrack, floatEl);
    else if (floatEl && localCam) attachTo(localCam, floatEl);

    if (remoteScreen) setStageKind('remote-screen');
    else if (localScreen) setStageKind('local-screen');
    else if (remoteCam) setStageKind('remote-cam');
    else setStageKind('idle');

    if (localCam && localEl) {
      attachTo(localCam, localEl);
      setCamOn(true);
    } else {
      setCamOn(Boolean(localCam && !localCam.isMuted));
    }

    if ((remoteScreen || localScreen) && remoteCam && remoteEl) {
      attachTo(remoteCam, remoteEl);
      setRemoteCamOn(true);
    } else {
      setRemoteCamOn(false);
    }

    setSharing(Boolean(localScreen));
  }, []);

  const refreshViewers = useCallback(() => {
    const room = roomRef.current;
    if (!room) {
      setViewers([]);
      return;
    }
    const list = [];
    const lp = room.localParticipant;
    list.push({
      identity: lp.identity,
      ...participantIdentity(lp),
      isLocal: true,
      quality: qualityRank(lp.connectionQuality),
      mic: lp.isMicrophoneEnabled,
      cam: lp.isCameraEnabled,
      screen: lp.isScreenShareEnabled,
      speaking: lp.isSpeaking,
    });
    room.remoteParticipants.forEach((p) => {
      list.push({
        identity: p.identity,
        ...participantIdentity(p),
        isLocal: false,
        quality: qualityRank(p.connectionQuality),
        mic: p.isMicrophoneEnabled,
        cam: p.isCameraEnabled,
        screen: p.isScreenShareEnabled,
        speaking: p.isSpeaking,
      });
    });
    setViewers(list);
  }, []);

  const stopLocalScreen = useCallback(async () => {
    const room = roomRef.current;
    const p = room?.localParticipant;
    for (const tr of screenTracksRef.current) {
      try {
        tr.stop();
        tr.detach();
      } catch {
        /* ignore */
      }
    }
    screenTracksRef.current = [];
    if (p) {
      try {
        await p.setScreenShareEnabled(false);
      } catch {
        /* ignore */
      }
      for (const source of [Track.Source.ScreenShare, Track.Source.ScreenShareAudio]) {
        const pub = p.getTrackPublication(source);
        if (pub?.track) {
          try {
            await p.unpublishTrack(pub.track);
            pub.track.stop();
          } catch {
            /* ignore */
          }
        }
      }
    }
    setSharing(false);
    syncLayout();
  }, [syncLayout]);

  const disconnect = useCallback(async () => {
    try {
      await stopLocalScreen();
    } catch {
      /* ignore */
    }
    try {
      document.querySelectorAll('[data-lk-audio="1"]').forEach((el) => el.remove());
      roomRef.current?.disconnect();
    } catch {
      /* ignore */
    }
    roomRef.current = null;
    connectKeyRef.current = '';
    connectInFlightRef.current = null;
    setMeta(null);
    setStatus('idle');
    setFatalError('');
    setWarn('');
    setMicOn(true);
    setCamOn(false);
    setSharing(false);
    setStageKind('idle');
    setRemoteCamOn(false);
    setViewers([]);
    setHands([]);
    setChat([]);
    setUnreadChat(0);
    setMinimized(false);
    setNetwork({
      quality: 'unknown',
      uplinkKbps: 0,
      downlinkKbps: 0,
      packetLoss: 0,
      rttMs: 0,
    });
  }, [stopLocalScreen]);

  const publishLocalMedia = useCallback(
    async (room) => {
      try {
        const audio = await createLocalAudioTrack();
        await room.localParticipant.publishTrack(audio);
        setMicOn(true);
      } catch (e) {
        setWarn((w) => w || `Microfone: ${mediaErrPt(e)}`);
        setMicOn(false);
      }
      try {
        const video = await createLocalVideoTrack({
          resolution: { width: 1280, height: 720 },
        });
        await room.localParticipant.publishTrack(video);
        setCamOn(true);
      } catch {
        setWarn((w) => w || 'Câmera indisponível — você pode ligar depois.');
        setCamOn(false);
      }
      syncLayout();
    },
    [syncLayout],
  );

  const ensureConnected = useCallback(
    async ({ mode, id, guestName = '', title = '' }) => {
      const key = `${mode}:${id}:${guestName || ''}`;

      if (connectKeyRef.current === key) {
        if (statusRef.current === 'connected' && roomRef.current) {
          setMinimized(false);
          syncLayout();
          refreshViewers();
          return;
        }
        if (connectInFlightRef.current) {
          return connectInFlightRef.current;
        }
      }

      if (roomRef.current && connectKeyRef.current !== key) {
        await disconnect();
      }

      const run = async () => {
        connectKeyRef.current = key;
        setStatus('connecting');
        setFatalError('');
        setWarn('');
        setMinimized(false);
        setMeta({
          mode,
          id,
          guestName,
          role: 'guest',
          title: title || (mode === 'live' ? t('meetings.room.live') : t('meetings.room.title')),
        });

        try {
          let tokenRes;
          const isLive = mode === 'live';
          if (isLive) {
            tokenRes = await axios.post(
              `${API_URL}/api/meetings/live/${id}/token`,
              {},
              { withCredentials: true },
            );
          } else if (guestName) {
            tokenRes = await axios.post(
              `${API_URL}/api/meetings/public/join/${id}/token`,
              null,
              { params: { guest_name: guestName }, withCredentials: true },
            );
          } else {
            tokenRes = await axios.post(
              `${API_URL}/api/meetings/bookings/${id}/token`,
              {},
              { withCredentials: true },
            );
          }
          const { token, url, livekit_configured, role: r } = tokenRes.data || {};
          setMeta((m) => (m ? { ...m, role: r || 'guest' } : m));
          if (!livekit_configured || !url || !token) {
            setFatalError(t('meetings.room.err.livekit'));
            setStatus('error');
            return;
          }

          const room = new Room({
            adaptiveStream: true,
            dynacast: true,
            disconnectOnPageLeave: true,
          });
          roomRef.current = room;

          const onLayout = () => {
            syncLayout();
            refreshViewers();
          };

          room.on(RoomEvent.TrackSubscribed, (track) => {
            if (track.kind === Track.Kind.Audio) {
              const a = track.attach();
              a.dataset.lkAudio = '1';
              document.body.appendChild(a);
            }
            onLayout();
          });
          room.on(RoomEvent.TrackUnsubscribed, onLayout);
          room.on(RoomEvent.LocalTrackPublished, onLayout);
          room.on(RoomEvent.LocalTrackUnpublished, onLayout);
          room.on(RoomEvent.TrackMuted, onLayout);
          room.on(RoomEvent.TrackUnmuted, onLayout);
          room.on(RoomEvent.ParticipantConnected, onLayout);
          room.on(RoomEvent.ParticipantDisconnected, onLayout);
          room.on(RoomEvent.ParticipantMetadataChanged, refreshViewers);
          room.on(RoomEvent.ParticipantNameChanged, refreshViewers);
          room.on(RoomEvent.ActiveSpeakersChanged, refreshViewers);
          room.on(RoomEvent.ConnectionQualityChanged, (_q, participant) => {
            if (!participant || participant === room.localParticipant) {
              setNetwork((n) => ({
                ...n,
                quality: qualityRank(room.localParticipant.connectionQuality),
              }));
            }
            refreshViewers();
          });
          room.on(RoomEvent.DataReceived, (payload, participant) => {
            try {
              const msg = JSON.parse(new TextDecoder().decode(payload));
              if (msg?.type === 'raise_hand') {
                const who = participant?.name || participant?.identity || msg.name || 'Alguém';
                setHands((prev) => (prev.includes(who) ? prev : [...prev, who]));
                toast.message(`${who} levantou a mão`);
              }
              if (msg?.type === 'chat' && msg.text) {
                const from = msg.name || participant?.name || participant?.identity || 'Participante';
                chatIdRef.current += 1;
                setChat((prev) => [
                  ...prev.slice(-199),
                  {
                    id: `c${chatIdRef.current}`,
                    from,
                    text: String(msg.text).slice(0, 500),
                    at: Date.now(),
                    self: false,
                  },
                ]);
                setUnreadChat((u) => u + 1);
              }
            } catch {
              /* ignore */
            }
          });
          room.on(RoomEvent.Disconnected, () => {
            if (connectKeyRef.current === key) {
              setStatus('idle');
              setMinimized(false);
            }
          });

          await room.connect(url, token, {
            websocketTimeout: 20000,
            peerConnectionTimeout: 15000,
          });

          refreshViewers();
          setStatus('connected');
          setNetwork((n) => ({
            ...n,
            quality: qualityRank(room.localParticipant.connectionQuality),
          }));
          syncLayout();

          const canPublish =
            r === 'host' || r === 'guest' || r === 'speaker' || (!isLive && r !== 'audience');
          if (canPublish && r !== 'audience') {
            void publishLocalMedia(room);
          }
        } catch (e) {
          if (connectKeyRef.current !== key) return;
          const detail = e?.response?.data?.detail;
          const raw = typeof detail === 'string' ? detail : e?.message || '';
          setFatalError(translateRoomError(raw, t));
          setStatus('error');
          try {
            roomRef.current?.disconnect();
          } catch {
            /* ignore */
          }
          roomRef.current = null;
        }
      };

      connectInFlightRef.current = run().finally(() => {
        if (connectInFlightRef.current) connectInFlightRef.current = null;
      });
      return connectInFlightRef.current;
    },
    [disconnect, publishLocalMedia, refreshViewers, syncLayout, t],
  );

  useEffect(() => {
    if (status !== 'connected') return undefined;
    const tick = async () => {
      const room = roomRef.current;
      if (!room) return;
      try {
        let outBytes = 0;
        let inBytes = 0;
        let packetsLost = 0;
        let rtt = 0;
        const pubs = [
          ...room.localParticipant.trackPublications.values(),
          ...[...room.remoteParticipants.values()].flatMap((p) => [...p.trackPublications.values()]),
        ];
        for (const pub of pubs) {
          const track = pub.track;
          if (!track) continue;
          try {
            if (track.sender?.getStats) {
              const report = await track.sender.getStats();
              const parsed = parseBitrateKbps(report, 'out');
              outBytes += parsed.bytes;
              if (parsed.rtt) rtt = parsed.rtt;
            }
            if (track.receiver?.getStats) {
              const report = await track.receiver.getStats();
              const parsed = parseBitrateKbps(report, 'in');
              inBytes += parsed.bytes;
              packetsLost += parsed.packetsLost;
              if (parsed.rtt) rtt = parsed.rtt;
            }
          } catch {
            /* ignore */
          }
        }
        const now = Date.now();
        const prev = bytesPrevRef.current;
        const dt = prev.t ? Math.max(0.5, (now - prev.t) / 1000) : 0;
        const uplinkKbps =
          dt && outBytes >= prev.out ? Math.round(((outBytes - prev.out) * 8) / 1000 / dt) : 0;
        const downlinkKbps =
          dt && inBytes >= prev.in ? Math.round(((inBytes - prev.in) * 8) / 1000 / dt) : 0;
        bytesPrevRef.current = { out: outBytes, in: inBytes, t: now };
        setNetwork((n) => ({
          ...n,
          uplinkKbps,
          downlinkKbps,
          packetLoss: packetsLost,
          rttMs: rtt || n.rttMs,
          quality: qualityRank(room.localParticipant.connectionQuality) || n.quality,
        }));
      } catch {
        /* ignore */
      }
    };
    tick();
    const iv = setInterval(tick, 2000);
    return () => clearInterval(iv);
  }, [status]);

  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (status === 'connected') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [status]);

  const registerMediaTargets = useCallback(
    (targets) => {
      mediaTargetsRef.current = { ...mediaTargetsRef.current, ...targets };
      syncLayout();
      return () => {
        Object.keys(targets).forEach((k) => {
          if (mediaTargetsRef.current[k] === targets[k]) {
            detachEl(mediaTargetsRef.current[k]);
            delete mediaTargetsRef.current[k];
          }
        });
      };
    },
    [syncLayout],
  );

  const registerFloatVideo = useCallback(
    (el) => {
      floatVideoRef.current = el;
      syncLayout();
      return () => {
        if (floatVideoRef.current === el) {
          detachEl(el);
          floatVideoRef.current = null;
        }
      };
    },
    [syncLayout],
  );

  const toggleMic = useCallback(async () => {
    const p = roomRef.current?.localParticipant;
    if (!p) return;
    try {
      const next = !micOn;
      await p.setMicrophoneEnabled(next);
      setMicOn(next);
      refreshViewers();
    } catch (e) {
      toast.error(mediaErrPt(e));
    }
  }, [micOn, refreshViewers]);

  const toggleCam = useCallback(async () => {
    const p = roomRef.current?.localParticipant;
    if (!p) return;
    try {
      if (!camOn) {
        const video = await createLocalVideoTrack({
          resolution: { width: 1280, height: 720 },
        });
        await p.publishTrack(video);
        setCamOn(true);
        setWarn('');
        syncLayout();
      } else {
        await p.setCameraEnabled(false);
        const pub = p.getTrackPublication(Track.Source.Camera);
        if (pub?.track) {
          try {
            await p.unpublishTrack(pub.track);
            pub.track.stop();
          } catch {
            /* ignore */
          }
        }
        setCamOn(false);
        syncLayout();
      }
      refreshViewers();
    } catch (e) {
      toast.error(`Câmera: ${mediaErrPt(e)}`);
      setCamOn(false);
    }
  }, [camOn, refreshViewers, syncLayout]);

  const toggleShare = useCallback(async () => {
    const p = roomRef.current?.localParticipant;
    if (!p) return;
    if (sharing) {
      await stopLocalScreen();
      toast.message('Compartilhamento encerrado');
      return;
    }
    try {
      const tracks = await createLocalScreenTracks({
        resolution: { width: 1920, height: 1080 },
        audio: true,
      });
      screenTracksRef.current = tracks;
      for (const track of tracks) {
        if (track.kind === Track.Kind.Video) {
          track.on(TrackEvent.Ended, () => {
            stopLocalScreen();
          });
        }
        await p.publishTrack(track, {
          source:
            track.kind === Track.Kind.Video
              ? Track.Source.ScreenShare
              : Track.Source.ScreenShareAudio,
          simulcast: false,
        });
      }
      setSharing(true);
      setWarn('');
      syncLayout();
      toast.success('Compartilhando tela');
    } catch (e) {
      await stopLocalScreen();
      const cancelled = /NotAllowed|Permission|denied|cancel/i.test(String(e?.message || e));
      if (cancelled) toast.message('Compartilhamento cancelado');
      else toast.error(`Tela: ${mediaErrPt(e)}`);
    }
  }, [sharing, stopLocalScreen, syncLayout]);

  const raiseHand = useCallback(async () => {
    const room = roomRef.current;
    if (!room?.localParticipant) return;
    const name = meta?.guestName || room.localParticipant.name || 'Participante';
    const payload = new TextEncoder().encode(JSON.stringify({ type: 'raise_hand', name }));
    await room.localParticipant.publishData(payload, { reliable: true });
    toast.success('Mão levantada — o anfitrião foi avisado');
  }, [meta?.guestName]);

  const sendChat = useCallback(
    async (text) => {
      const room = roomRef.current;
      const trimmed = String(text || '').trim();
      if (!room?.localParticipant || !trimmed) return;
      const name = room.localParticipant.name || meta?.guestName || 'Você';
      const payload = new TextEncoder().encode(
        JSON.stringify({ type: 'chat', text: trimmed.slice(0, 500), name }),
      );
      await room.localParticipant.publishData(payload, { reliable: true });
      chatIdRef.current += 1;
      setChat((prev) => [
        ...prev.slice(-199),
        {
          id: `c${chatIdRef.current}`,
          from: name,
          text: trimmed.slice(0, 500),
          at: Date.now(),
          self: true,
        },
      ]);
    },
    [meta?.guestName],
  );

  const minimize = useCallback(() => {
    if (status === 'connected') {
      setMinimized(true);
      setDockExpanded(true);
    }
  }, [status]);

  const expand = useCallback(() => {
    setMinimized(false);
  }, []);

  const clearUnread = useCallback(() => setUnreadChat(0), []);

  const value = useMemo(
    () => ({
      meta,
      status,
      fatalError,
      warn,
      micOn,
      camOn,
      sharing,
      stageKind,
      remoteCamOn,
      viewers,
      hands,
      chat,
      unreadChat,
      network,
      minimized,
      dockExpanded,
      setDockExpanded,
      path: meta ? roomPath(meta.mode, meta.id) : null,
      active: status === 'connected' || status === 'connecting',
      ensureConnected,
      disconnect,
      minimize,
      expand,
      toggleMic,
      toggleCam,
      toggleShare,
      raiseHand,
      sendChat,
      clearUnread,
      registerMediaTargets,
      registerFloatVideo,
      syncLayout,
    }),
    [
      meta,
      status,
      fatalError,
      warn,
      micOn,
      camOn,
      sharing,
      stageKind,
      remoteCamOn,
      viewers,
      hands,
      chat,
      unreadChat,
      network,
      minimized,
      dockExpanded,
      ensureConnected,
      disconnect,
      minimize,
      expand,
      toggleMic,
      toggleCam,
      toggleShare,
      raiseHand,
      sendChat,
      clearUnread,
      registerMediaTargets,
      registerFloatVideo,
      syncLayout,
    ],
  );

  return (
    <MeetingSessionContext.Provider value={value}>{children}</MeetingSessionContext.Provider>
  );
}

export function useMeetingSession() {
  const ctx = useContext(MeetingSessionContext);
  if (!ctx) {
    throw new Error('useMeetingSession must be used within MeetingSessionProvider');
  }
  return ctx;
}

export function useMeetingSessionOptional() {
  return useContext(MeetingSessionContext);
}
